import * as types from '@phoenix/store/mutations-types';
import { get, isEmpty, reduce, set } from 'lodash-es';
import { TAB_ALL, TAB_PRODUCTS, TABS_ORDER } from '@maison/helpers/search-helper';
import { clientApi } from '@phoenix/clientApi';
import { searchClient } from '@maison/search/search';
import { DEFAULT_SORT } from '@phoenix/helpers/mixed-grid-helper';

const getInitialTabState = () => ({
  dataFullyLoaded: false,
  isLoading: false,
  data: [],
  total: 0,
  currentPage: 0,
  currentSort: DEFAULT_SORT,
  filters: [],
  activeFilters: {},
});

const getInitialTabsState = () => {
  const tabs = {};
  TABS_ORDER.forEach((tab) => (tabs[tab] = getInitialTabState()));
  return tabs;
};

const initialState = {
  query: '',
  stagedQuery: '',
  isLoading: false,
  isResultsComponentPresent: false,
  currentTab: TABS_ORDER[0],
  tabs: getInitialTabsState(),
  suggestions: [],
  searchAsYouTypeEndpoint: 'api.search.searchAsYouType',
};

const getters = {
  totalResults: (state) => reduce(state.tabs, (result, item) => result + item.total, 0),
  totalSuggestionsResults: (state) => state.suggestions.length,
};

const actions = {
  async setQuery({ commit, dispatch, state }, query) {
    if (state.query === query) {
      return;
    }

    commit(types.SEARCH_SET_LOADING, true);
    commit(types.SEARCH_SET_QUERY, query);
    commit(types.SEARCH_CHANGE_CURRENT_TAB, TABS_ORDER[0]);
    await dispatch('loadData', { clearData: true, tab: TAB_ALL });

    commit(types.SEARCH_SET_LOADING, false);
  },

  async fetchSuggestions({ commit, state }, query) {
    const { data } = await clientApi.get(window.route(state.searchAsYouTypeEndpoint, { query }));
    commit(types.SEARCH_SET_SUGGESTIONS_RESULTS, data.data.results);
  },

  changeCurrentTab({ commit, dispatch, state }, tab) {
    tab = tab.split('-').pop();

    if (state.currentTab === tab) {
      return;
    }

    commit(types.SEARCH_CHANGE_CURRENT_TAB, tab);

    dispatch('loadData', { clearData: false, tab });

    window.scrollTo(0, 0);
  },

  async loadData({ commit, state, getters }, { clearData, tab }) {
    if (isEmpty(state.query)) {
      return;
    }

    // We do a copy of the value in case it changes
    const query = ` ${state.query}`.slice(1);

    if (clearData) {
      commit(types.SEARCH_RESET_RESULTS, tab);
    }

    if (state.tabs[tab]?.dataFullyLoaded) {
      return;
    }

    commit(types.SEARCH_SET_TAB_LOADING, { tab, isLoading: true });

    let page = null;
    let shouldStoreFilters = false;
    if (tab === TAB_ALL) {
      shouldStoreFilters = true;
    } else {
      page = state.tabs[tab].currentPage ? state.tabs[tab].currentPage + 1 : 1;
    }

    const options = {
      page,
      settings: [
        {
          name: tab,
          sort: state.tabs[tab]?.currentSort,
          filters: state.tabs[tab]?.activeFilters,
        },
      ],
    };

    try {
      const data = await searchClient.performMainSearch(query, tab, options);

      if (data.type === 'redirect') {
        await new Promise(() => (window.location.href = data.redirectUrl));
        return;
      }

      if (data.type === 'aggregated') {
        Object.keys(data.results).forEach((key) => {
          if (state.tabs[key].dataFullyLoaded) {
            return;
          }

          commit(types.SEARCH_SET_TAB_RESULTS, { tab: key, data: { ...data.results[key] }, shouldStoreFilters });
        });
      } else {
        commit(types.SEARCH_SET_TAB_RESULTS, { tab, data, shouldStoreFilters });
      }

      window.tracking.pushEvent(window.tracking.getSearchResultsTracking(query, getters.totalResults));
    } catch (error) {}

    commit(types.SEARCH_SET_TAB_LOADING, { tab, isLoading: false });
  },

  resultComponentIsPresent({ commit }) {
    commit(types.SEARCH_SET_RESULT_COMPONENT_PRESENCE, {
      isResultsComponentPresent: true,
    });
  },

  setSearchAsYouTypeEndpoint({ commit }, endpoint = '') {
    endpoint !== '' && commit(types.SEARCH_SET_SEARCH_AS_YOU_TYPE_ENDPOINT, endpoint);
  },

  setSortOption({ commit }, { tab, sortOption }) {
    commit(types.SEARCH_SET_TAB_SORT_OPTION, { tab, sortOption });
  },

  setActiveFilters({ commit }, { tab, filters }) {
    commit(types.SEARCH_SET_TAB_ACTIVE_FILTERS, { tab, filters });
  },
};

const mutations = {
  [types.SEARCH_SET_QUERY](state, query) {
    state.query = query;
  },

  [types.SEARCH_SET_LOADING](state, isLoading) {
    state.isLoading = isLoading;
  },

  [types.SEARCH_CHANGE_CURRENT_TAB](state, tab) {
    state.currentTab = tab;
  },

  [types.SEARCH_RESET_RESULTS](state, tab) {
    if (tab !== TAB_ALL) {
      set(state.tabs, [tab, 'dataFullyLoaded'], false);
      set(state.tabs, [tab, 'data'], []);
      set(state.tabs, [tab, 'currentPage'], 0);
      return;
    }

    for (const tab in state.tabs) {
      set(state.tabs, [tab], getInitialTabState());
    }
  },

  [types.SEARCH_SET_TAB_LOADING](state, { tab, isLoading }) {
    if (tab !== TAB_ALL) {
      set(state.tabs, [tab, 'isLoading'], isLoading);
    }
  },

  [types.SEARCH_SET_TAB_RESULTS](state, { tab, data, shouldStoreFilters = false }) {
    // Set the data
    if (data.meta && data.meta.current_page === 1) {
      set(state.tabs, [tab, 'data'], data.data);
    } else {
      set(state.tabs, [tab, 'data'], state.tabs[tab].data.concat(data.data));
    }

    const total = data.meta ? data.meta.total : data.total;
    set(state.tabs, [tab, 'total'], total);

    if (data.meta && data.meta.current_page) {
      set(state.tabs, [tab, 'currentPage'], data.meta.current_page);
    }

    // The all tab is always fully loaded
    if (state.currentTab === TAB_ALL) {
      set(state.tabs, [TAB_ALL, 'dataFullyLoaded'], true);
    }

    if (tab === TAB_PRODUCTS && data.hasOwnProperty('suggestedCollection')) {
      set(state.tabs, [tab, 'suggestedCollection'], data.suggestedCollection);
    }

    if (shouldStoreFilters) {
      set(state.tabs, [tab, 'filters'], get(data, 'filters', []));
    }

    // Check if this is the last page or if we have all items (ALL TAB)
    if (
      (data.meta && data.meta.current_page && data.meta.current_page === data.last_page) ||
      state.tabs[tab].data.length === state.tabs[tab].total
    ) {
      set(state.tabs, [tab, 'dataFullyLoaded'], true);
    }
  },

  [types.SEARCH_SET_RESULT_COMPONENT_PRESENCE](state, { isResultsComponentPresent }) {
    state.isResultsComponentPresent = isResultsComponentPresent;
  },

  [types.SEARCH_SET_SUGGESTIONS_RESULTS](state, suggestions) {
    state.suggestions = suggestions;
  },

  [types.SEARCH_SET_SEARCH_AS_YOU_TYPE_ENDPOINT](state, endpoint) {
    state.searchAsYouTypeEndpoint = endpoint;
  },

  [types.SEARCH_SET_STAGED_QUERY](state, stagedQuery) {
    state.stagedQuery = stagedQuery;
  },

  [types.SEARCH_SET_TAB_SORT_OPTION](state, { tab, sortOption }) {
    set(state.tabs, [tab, 'currentSort'], sortOption);
  },

  [types.SEARCH_SET_TAB_ACTIVE_FILTERS](state, { tab, filters }) {
    set(state.tabs, [tab, 'activeFilters'], JSON.parse(JSON.stringify(filters)));
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
